import { useTranslations } from "@jugl-web/utils";
import { Button } from "@jugl-web/ui-components/cross-platform";
import { useNavigation } from "@web-src/modules/navigation/hooks/useNavigation";
import { useEffect, useMemo } from "react";
import { useEntitiesListProvider } from "@web-src/features/entities/providers/EntitiesListProvider/useEntitiesListProvider";
import { useUserAuth } from "@web-src/modules/auth/providers/UserAuthProvider/useUserAuth";
import { getStorageItem, JUGL_LOGIN } from "@jugl-web/utils/storage";
import { useRestApiProvider } from "@jugl-web/rest-api";
import { SignUpFlowLayout } from "../SignUpFlowLayout";
import { SignUpFlowContentLayout } from "../../components/SignUpFlowContentLayout/SignUpFlowContentLayout";

export const SignUpNoInvitations = () => {
  const { t } = useTranslations();
  const { logout } = useUserAuth();
  const { authApi } = useRestApiProvider();

  const { navigateToPage, navigateBack } = useNavigation();
  const { data: loginMethodList } = authApi.useListLoginMethodsQuery();

  const {
    entitiesState: { entities },
    entityInvitesState: { entityInvites },
  } = useEntitiesListProvider();

  useEffect(() => {
    if (entityInvites?.length) {
      navigateToPage("authInvitations");
    }
  }, [entityInvites?.length, navigateToPage]);

  const login = useMemo(() => {
    const storedLogin: string = getStorageItem(JUGL_LOGIN) || "";
    if (storedLogin) {
      return storedLogin;
    }
    const fallbackLogin = loginMethodList?.find(
      (method) => method.type === "email"
    )?.value;
    return fallbackLogin || loginMethodList?.[0]?.value || "";
  }, [loginMethodList]);

  return (
    <SignUpFlowLayout type="noInvitations">
      <SignUpFlowContentLayout
        titleSectionClassName="w-full"
        title={t({
          id: "sign-up-no-invitations-page.title",
          defaultMessage: "No invite found - let’s fix that!",
        })}
        subtitle={
          <div className="w-[493px] px-4">
            {t({
              id: "sign-up-no-invitations-page.subtitle",
              defaultMessage:
                "It looks like this account isn’t linked to your team’s workspace yet. Here's how you can get back on track 👇",
            })}
          </div>
        }
        footer={
          <div className="flex gap-[64px] pt-[64px]">
            <Button
              className="mx-auto w-[138px]"
              type="submit"
              variant="outlined"
              onClick={() => {
                if (entities?.length) {
                  navigateBack();
                } else {
                  navigateToPage("authSignUpPurpose");
                }
              }}
            >
              {t({
                id: "sign-up-no-invitations-page.go-back",
                defaultMessage: "Go Back",
              })}
            </Button>
            <Button
              className="mx-auto w-[300px]"
              type="submit"
              onClick={() => {
                logout(false);
              }}
            >
              {t({
                id: "sign-up-no-invitations-page.try-another-email-or-phone",
                defaultMessage: "Try Another Email or Phone",
              })}
            </Button>
          </div>
        }
        description={
          <div className="flex flex-col items-center font-secondary">
            <div className="flex flex-col gap-[32px] 2xl:gap-[64px]">
              <div className="flex gap-4">
                <div className="relative mt-1 flex h-[42px] w-[42px] shrink-0 items-center justify-center">
                  <div className="absolute inset-0 rounded-full bg-gradient-to-br from-[#2196F3] to-[#FDABE8]" />

                  <div className="absolute inset-[2px] rounded-full bg-white" />

                  <div className="relative z-10 flex h-full w-full items-center justify-center font-medium text-dark">
                    1
                  </div>
                </div>{" "}
                <div className="flex flex-col">
                  <div className="mt-1 text-lg font-medium text-dark">
                    {t({
                      id: "sign-up-no-invitations-page.check-if-sent",
                      defaultMessage: "Check if your invite was sent yet",
                    })}
                  </div>
                  <div className="w-[442px] text-dark-600">
                    {t({
                      id: "sign-up-no-invitations-page.check-if-sent-description",
                      defaultMessage:
                        "Your team might still need to send it - worth double-checking!",
                    })}
                  </div>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="relative mt-1 flex h-[42px] w-[42px] shrink-0 items-center justify-center">
                  <div className="absolute inset-0 rounded-full bg-gradient-to-br from-[#2196F3] to-[#FDABE8]" />

                  <div className="absolute inset-[2px] rounded-full bg-white" />

                  <div className="relative z-10 flex h-full w-full items-center justify-center font-medium text-dark">
                    2
                  </div>
                </div>{" "}
                <div className="flex flex-col">
                  <div className="mt-1 text-lg font-medium text-dark">
                    {t({
                      id: "sign-up-no-invitations-page.change-login",
                      defaultMessage: "Log in the way the invite was sent",
                    })}
                  </div>
                  <div className="w-[442px] pr-10 text-dark-600">
                    {t({
                      id: "sign-up-no-invitations-page.change-login-description",
                      defaultMessage:
                        "If it was sent to your phone, log in with your phone. If it was sent to your email, use that instead.",
                    })}
                  </div>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1 pt-[32px] text-dark-600 2xl:pt-[64px]">
              <div>
                {t({
                  id: "sign-up-no-invitations-page.currently-logged-as",
                  defaultMessage: "You’re currently logged in as:",
                })}
              </div>
              <div className="font-medium text-primary-800">{login}</div>
            </div>
          </div>
        }
      />
    </SignUpFlowLayout>
  );
};
